import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { Bell, Menu, User as UserIcon, LogOut, Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { NotificationCenter } from '@/components/NotificationCenter'
import { User } from '@/models/user.model'
import { paths } from '@/routes/path'
interface HeaderProps {
  user: User | null
  onToggleSidebar?: () => void
  isSidebarOpen?: boolean
}

export function Header({ user, onToggleSidebar, isSidebarOpen }: HeaderProps) {
  const { logout } = useAuth()
  const { unreadCount } = useNotifications()
  const navigate = useNavigate()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false)

  const handleGoToProfile = () => {
    setIsUserMenuOpen(false)
    navigate(paths.app.profile.absolute)
  }

  const handleLogout = () => {
    logout()
    setIsUserMenuOpen(false)
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="px-4 sm:px-6 py-4">
        <div className="flex justify-between items-center gap-4 flex-wrap md:flex-nowrap">
          {/* Left side - Greeting */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="mt-1 lg:hidden"
              onClick={onToggleSidebar}
              aria-label="Toggle navigation"
              aria-expanded={isSidebarOpen}
              aria-controls="navigation"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Hello {user?.firstName} 👋
              </h1>
              <p className="text-sm font-medium text-gray-700 sm:hidden">
                Welcome Back
              </p>
              <p className="text-lg text-gray-900 font-semibold hidden sm:block">
                Welcome Back
              </p>
            </div>
          </div>

          {/* Right side - User profile and notifications */}
          <div className="flex items-center space-x-2 sm:space-x-4 ml-auto">
            {/* Notifications */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setIsNotificationCenterOpen(true)}
            >
              <span className="sr-only">View notifications</span>
              <Bell className="h-6 w-6 text-gray-600" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></div>
              )}
            </Button>

            {/* User profile */}
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-gray-600 capitalize">
                  {user?.role}
                </div>
              </div>
              <div className="relative">
                <Button
                  variant="ghost"
                  className="p-0"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <Avatar className="h-10 w-10 bg-primary-500">
                    <AvatarImage src={user?.avatar??''} alt={user?.firstName??''} />
                    <AvatarFallback className="bg-primary-500 text-white text-sm font-semibold">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </Button>

                {/* Dropdown menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-sm text-gray-600">{user?.email}</p>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {user?.role}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      className="w-full justify-start px-4 py-2 h-auto text-gray-700 hover:bg-gray-50"
                      onClick={handleGoToProfile}
                    >
                      <UserIcon className="h-4 w-4 mr-3" />
                      Your Profile
                    </Button>
                    {/* <Button
                      variant="ghost"
                      className="w-full justify-start px-4 py-2 h-auto text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Settings
                    </Button> */}
                    <Button
                      variant="ghost"
                      className="w-full justify-start px-4 py-2 h-auto text-red-600 hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign out
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Center */}
      <NotificationCenter
        isOpen={isNotificationCenterOpen}
        onClose={() => setIsNotificationCenterOpen(false)}
      />
    </header>
  )
}
